import React, { useMemo, useEffect } from 'react';
import { throttle } from 'lodash';

import { Container, Form, Row, Col } from 'react-bootstrap';
import styles from './EditSolicitacaoExames.module.scss';
import { useConsulta } from '../contexts/ConsultaContext';
import FormLabel from './FormLabel';
import AsyncSelect from 'react-select/async';
import Button from './Button';
import { fetchAuth } from '../utils/fetchAuth';

const apiUrl = process.env.REACT_APP_API_URL || '';


const EditSolicitacaoExames: React.FC = () => {
    const { consulta, setConsulta } = useConsulta();
    const [saving, setSaving] = React.useState(false);

    const modified = (() => {
        const edited = consulta?.solicitacao_exames?.last_edited;
        const updated = consulta?.solicitacao_exames?.last_updated;

        if (!edited || !updated) return false;

        return new Date(edited) > new Date(updated);
    })();

    const loadOptions = async (inputValue: string) => {
        if (inputValue.length < 0) return [];

        const response = await fetchAuth(`${apiUrl}/consultorio/exams?term=${inputValue}`);

        if (!response.ok) {
            const errorData = await response.json();
            console.error('Erro ao buscar exames:', errorData.message || response.statusText);
            return [];
        }

        const json = await response.json();

        return json.map((item: any) => ({
            value: item,
            label: item.text,
        }));
    };

    // Crie uma função com throttle (evita chamadas excessivas ao digitar)
    const throttledLoadOptions = useMemo(
        () => throttle(loadOptions, 500), // 500ms entre chamadas
        []
    );

    useEffect(() => {
        return () => {
            throttledLoadOptions.cancel(); // evita vazamento de memória
        };
    }, [throttledLoadOptions]);

    return (
        <>
            <Container>
                <h5 className="mb-3">Solicitação de Exames</h5>
            </Container>
            <Container>
                <Row>
                    <Col>
                        <Form.Group className="mb-3">
                            <FormLabel>
                                Exames solicitados:
                            </FormLabel>
                            <AsyncSelect
                                cacheOptions
                                loadOptions={throttledLoadOptions}
                                defaultOptions={false}
                                classNames={{
                                    control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                                    menu: () => styles['select-menu'],
                                    option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                                    container: () => styles['select-container'],
                                }}
                                isMulti
                                placeholder="Selecione os exames..."
                                onChange={(selectedOptions) => {
                                    let updatedConsulta : Consultas = consulta ? { ...consulta } : {
                                        id: -1,
                                        title: '',
                                        last_modified: (new Date().toISOString()),
                                        last_edited: (new Date().toISOString()),
                                    };

                                    if (!updatedConsulta.solicitacao_exames) {
                                        updatedConsulta.solicitacao_exames = {
                                            id: -1,
                                            exames_solicitados: [],
                                            observacoes_gerais: '',
                                            last_updated: (new Date(Date.now() - 3 * 60 * 1000).toISOString()),
                                            last_edited: (new Date().toISOString()),
                                        };
                                    }

                                    const examesSolicitados = selectedOptions ? selectedOptions.map(option => option.value) : [];

                                    updatedConsulta.last_edited = (new Date().toISOString());

                                    updatedConsulta.solicitacao_exames.exames_solicitados = examesSolicitados;
                                    updatedConsulta.solicitacao_exames.last_edited = (new Date().toISOString());

                                    setConsulta(updatedConsulta);
                                }}
                                value={(consulta?.solicitacao_exames?.exames_solicitados ?? []).map(exame => ({
                                    value: exame,
                                    label: exame.text,
                                }))}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group className="mb-3">
                            <FormLabel
                                htmlFor='observacoesGerais'
                            >
                                Observações gerais:
                            </FormLabel>
                            <textarea
                                id='observacoesGerais'
                                className={[styles['text-area-control'], 'form-control'].join(' ')}
                                rows={3}
                                defaultValue={consulta?.solicitacao_exames?.observacoes_gerais || ''}
                                onChange={(e) => {
                                    const now = new Date().toISOString();

                                    let updatedConsulta : Consultas = consulta ? { ...consulta } : {
                                        id: -1,
                                        title: '',
                                        last_modified: now,
                                        last_edited: now,
                                    };

                                    if (!updatedConsulta.solicitacao_exames) {
                                        updatedConsulta.solicitacao_exames = {
                                            id: -1,
                                            exames_solicitados: [],
                                            observacoes_gerais: '',
                                            last_updated: now,
                                            last_edited: now,
                                        };
                                    }

                                    updatedConsulta.last_edited = now;

                                    updatedConsulta.solicitacao_exames.observacoes_gerais = e.target.value;
                                    updatedConsulta.solicitacao_exames.last_edited = now;

                                    setConsulta(updatedConsulta);
                                }}
                            ></textarea>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Button
                            disabled={!modified || saving}
                            onClick={() => {
                                if (!consulta) return;
                                setSaving(true);
                                fetchAuth(`${process.env.REACT_APP_API_URL}/consultorio/consultations/${consulta.id}/exams`, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                        exames: (consulta?.solicitacao_exames?.exames_solicitados || []).map(
                                            exame => exame
                                        ),
                                        observacoes: consulta?.solicitacao_exames?.observacoes_gerais || '',
                                    }),
                                })
                                    .then(async response => {
                                        if (!response.ok) {
                                            const errorData = await response.json();
                                            throw new Error(errorData.message || 'Erro ao salvar solicitação de exames');
                                        }
                                        return await response.json();
                                    })
                                    .then(data => {
                                        if (!consulta) return;
                                        setConsulta({
                                            ...consulta,
                                            solicitacao_exames: {
                                                id: -1,
                                                exames_solicitados: (consulta?.solicitacao_exames?.exames_solicitados || []).map(
                                                    exame => exame
                                                ),
                                                observacoes_gerais: consulta?.solicitacao_exames?.observacoes_gerais || '',
                                                last_edited: (new Date()).toISOString(),
                                                last_updated: (new Date()).toISOString(),
                                            },
                                        });

                                    })
                                    .catch(error => {
                                        console.error('Erro ao salvar:', error);
                                    })
                                    .finally(() => {
                                        setSaving(false);
                                    });

                            }}
                        >
                            {modified ? (saving ? 'Salvando' : 'Salvar') : 'Salvo'}
                        </Button>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default EditSolicitacaoExames;
